import { InputAddon, InputGroup, InputWrapper } from '@eigtech/ui/components/input'
import { MaskedInput } from '@eigtech/ui/components/masked-input'
import { FieldValues, useController } from 'react-hook-form'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  inputProps?: React.ComponentProps<typeof MaskedInput>
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
} & BaseProps<TFieldValues>

export type MaskedInputFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameAndDefaultValueProps<Props<TFieldValues>, TFieldValues> &
    Pick<React.ComponentProps<typeof MaskedInput>, 'mask'>

export function MaskedInputField<TFieldValues extends FieldValues = FieldValues>(
  props: MaskedInputFieldProps<TFieldValues>
) {
  const {
    defaultValue,
    name,
    control,
    label,
    inputProps,
    leftAddon,
    rightAddon,
    leftElement,
    rightElement,
    mask,
    ...rest
  } = props

  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  return (
    <FieldControl control={control} label={label} name={name} {...rest}>
      <InputGroup>
        {typeof leftAddon === 'string' ? <InputAddon>{leftAddon}</InputAddon> : leftAddon}

        <InputWrapper>
          {leftElement}

          <MaskedInput
            {...field}
            id={name}
            {...inputProps}
            disabled={isSubmitting ?? rest.disabled ?? inputProps?.disabled}
            mask={mask}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e)
            }}
          />

          {rightElement}
        </InputWrapper>

        {typeof rightAddon === 'string' ? <InputAddon>{rightAddon}</InputAddon> : rightAddon}
      </InputGroup>
    </FieldControl>
  )
}

export type MaskedInputFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof MaskedInputField<TFieldValues>
